import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { CircularProgress } from "@mui/material";
import moment from "moment";

import FormSwitch from "../../Components/Form Fields/switch.js";
import DatePickerField from "../../Components/Form Fields/date-picker-field.js";
import PrimaryButton from "../../Components/Buttons/primary-button.js";

import renderGetRecurringDepositAmountDetails from "../../Organisms/Modals/Add Funds/get-recurring-deposit-amount-details.js";
import renderAuthorizeTransfer from "../../Organisms/Modals/Transfer/authorize-transfer.js";
import renderStatusModal from "../../Organisms/Modals/status-modal.js";
import renderConfirmClearRecurringDeposit from "../../Organisms/Modals/Recurring Deposits/confirm-clear-recurring-deposit.js";
import renderMandateBankAccountAddition from "../../Organisms/Modals/Bank Account/mandate-bank-account-addition.js";

import withFormProvider from "../../HOC/withFormProvider.js";

import { useModalContext } from "../../Contexts/ModalContext.js";

import {
  useConnectedAccounts,
  useDeals,
  useGetRecurringDeposit,
  useStakeDebit,
} from "../../CustomHooks/Queries/index.js";
import { useActivateRecurringDeposit } from "../../CustomHooks/Mutations/index.js";

import {
  appendISOTime,
  capitaliseString,
  formatCurrency,
  formatDate,
  getNumberWithOrdinal,
  getRecurringDepositDefaultPreferences,
} from "../../Utils/helpers.js";
import { getLastFour } from "../../Utils/pii.js";
import {
  FailedRecurringDepositStatusModalProps,
  longMonthDateFormat,
  nilYearLongMonthDateFormat,
  SuccessfulRecurringDepositStatusModalProps,
} from "../../Utils/constants.js";

import { ChevronLeft } from "../../Assets/Icons/index.js";

import "./recurring-deposits.scss";

const getRecurringDepositFrequencies = (d) => {
  const date = moment(d);
  const dayOfTheWeek = date.format("dddd");
  const dateOfTheMonth = getNumberWithOrdinal(date.date());

  return [
    { title: "Every Week", description: `Deposits every ${dayOfTheWeek}.`, value: "Weekly", dueOn: dayOfTheWeek },
    {
      title: "Every Two Weeks",
      description: `Deposits every second ${dayOfTheWeek}.`,
      value: "Biweekly",
      dueOn: dayOfTheWeek,
    },
    {
      title: "Every Month",
      description: `Deposits on the ${dateOfTheMonth} of each month.`,
      value: "Monthly",
      dueOn: dateOfTheMonth,
    },
  ];
};

export default function RecurringDepositsPage() {
  const recurringDepositAttributesRef = useRef(null);

  const navigate = useNavigate();

  const modalContext = useModalContext();

  const { onModalClose } = modalContext;

  const { isLoading: isLoadingDeals, data: deals } = useDeals();

  const { isLoading: isLoadingStakeDebit, data: stakeDebit } = useStakeDebit();

  const { isLoading: isLoadingConnectedAccounts, data: connectedAccounts } = useConnectedAccounts();

  const {
    isLoading: isLoadingRecurringDeposit,
    data: recurringDeposit,
    refetch: refetchRecurringDeposit,
  } = useGetRecurringDeposit();

  const isBankAccountConnected = useMemo(() => {
    return connectedAccounts?.data?.data?.accounts?.length > 0;
  }, [connectedAccounts?.data?.data?.accounts]);

  const onAddCashSuccess = (description) =>
    renderStatusModal(modalContext, {
      ...SuccessfulRecurringDepositStatusModalProps,
      description,
      onCTAButtonClick: () => onModalClose(),
      onSecondaryCTAButtonClick: () => onModalClose({ callOnClose: () => navigate("/home") }),
    });

  const openAuthorizeTransferModal = () =>
    renderAuthorizeTransfer(modalContext, {
      stake_account_number_last4: getLastFour(stakeDebit?.data?.data?.account_number),
      stake_routing_number: stakeDebit?.data?.data?.routing_number,
      clearingTransactionDate: deals?.data?.content?.transaction_clearing_date,
      isRecurringDeposit: true,
      amount: recurringDepositAttributesRef.current?.preferences?.amount,
      selectedAccount: recurringDepositAttributesRef.current?.selectedAccount,
      recurringDepositPreferences: recurringDepositAttributesRef.current?.preferences,
      AuthorizeCallback: () => ActivateRecurringDeposit(recurringDepositAttributesRef.current?.preferences),
    });

  const { mutate: ActivateRecurringDeposit } = useActivateRecurringDeposit({
    onSuccess: (_, v) => {
      onAddCashSuccess(
        `Your first deposit is scheduled for ${formatDate(appendISOTime(v.start_date), nilYearLongMonthDateFormat)}.`
      );
      refetchRecurringDeposit();
    },
    onError: () =>
      renderStatusModal(modalContext, {
        ...FailedRecurringDepositStatusModalProps,
        onCTAButtonClick: () => openAuthorizeTransferModal(),
        onSecondaryCTAButtonClick: () => onModalClose(),
      }),
  });

  const onSubmit = ({ startDate, frequency }) => {
    renderGetRecurringDepositAmountDetails(modalContext, {
      onFormSubmit: ({ cashAmount, selectedAccount }) => {
        recurringDepositAttributesRef.current = {
          selectedAccount,
          preferences: {
            amount: cashAmount,
            frequency_type: frequency,
            start_date: startDate,
            originating_account_id: selectedAccount.account_id,
          },
        };
        openAuthorizeTransferModal();
      },
    });
  };

  const isLoading = isLoadingDeals || isLoadingStakeDebit || isLoadingConnectedAccounts || isLoadingRecurringDeposit;

  const [animReady, setAnimReady] = useState(false);
  const hasTriggeredAnim = useRef(false);

  useEffect(() => {
    if (!isLoading && !hasTriggeredAnim.current) {
      hasTriggeredAnim.current = true;
      requestAnimationFrame(() => setAnimReady(true));
    }
  }, [isLoading]);

  return (
    <div className={`rdp${animReady ? " anim-ready" : ""}`}>
      <div className="rdp-header">
        <button className="rdp-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
          <ChevronLeft />
        </button>
        <h3 className="rdp-title">Recurring Deposit</h3>
        <div className="rdp-header-spacer" />
      </div>
      {isLoading ? (
        <div className="rdp-loading">
          <CircularProgress />
        </div>
      ) : (
        <RecurringDepositsFormWrapped
          recurringDepositAPIData={recurringDeposit?.data?.data}
          isBankAccountConnected={isBankAccountConnected}
          onSubmit={onSubmit}
        />
      )}
    </div>
  );
}

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="3" width="12" height="11" rx="2" stroke="#636879" strokeWidth="1.2" />
    <path d="M2 7h12" stroke="#636879" strokeWidth="1.2" />
    <path d="M5.5 1.5v3M10.5 1.5v3" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M5 3l4 4-4 4" stroke="#636879" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InlineCalendar = ({ selectedDate, onSelect, minDate }) => {
  const [viewMonth, setViewMonth] = useState(() => moment(selectedDate || undefined));

  const today = useMemo(() => moment().startOf("day"), []);
  const min = useMemo(() => (minDate ? moment(minDate).startOf("day") : today), [minDate, today]);

  const daysInMonth = viewMonth.daysInMonth();
  const firstDayOfWeek = moment(viewMonth).startOf("month").day();
  const prevMonthDays = moment(viewMonth).subtract(1, "month").daysInMonth();

  const weeks = useMemo(() => {
    const rows = [];
    let dayCounter = 1;
    let nextMonthDay = 1;

    for (let week = 0; week < 6; week++) {
      const row = [];
      for (let dow = 0; dow < 7; dow++) {
        const cellIndex = week * 7 + dow;
        if (cellIndex < firstDayOfWeek) {
          row.push({ day: prevMonthDays - firstDayOfWeek + cellIndex + 1, type: "prev" });
        } else if (dayCounter <= daysInMonth) {
          row.push({ day: dayCounter, type: "current" });
          dayCounter++;
        } else {
          row.push({ day: nextMonthDay, type: "next" });
          nextMonthDay++;
        }
      }
      rows.push(row);
      if (dayCounter > daysInMonth) break;
    }
    return rows.filter((row) => row.some((c) => c.type === "current"));
  }, [viewMonth, daysInMonth, firstDayOfWeek, prevMonthDays]);

  const selectedMoment = useMemo(() => (selectedDate ? moment(selectedDate).startOf("day") : null), [selectedDate]);

  return (
    <div className="rd-calendar">
      <div className="rd-cal-header">
        <button type="button" className="rd-cal-nav" onClick={() => setViewMonth((v) => moment(v).subtract(1, "month"))} aria-label="Previous month">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 4l-4 4 4 4" stroke="#E8EAF0" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <span className="rd-cal-month-label">{viewMonth.format("MMMM YYYY")}</span>
        <button type="button" className="rd-cal-nav" onClick={() => setViewMonth((v) => moment(v).add(1, "month"))} aria-label="Next month">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 4l4 4-4 4" stroke="#E8EAF0" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <div className="rd-cal-day-headers">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <span key={i} className="rd-cal-day-header">{d}</span>
        ))}
      </div>
      <div className="rd-cal-grid">
        {weeks.map((row, wi) => (
          <div key={wi} className="rd-cal-row">
            {row.map((cell, ci) => {
              const cellDate = moment(viewMonth).startOf("month");
              if (cell.type === "prev") cellDate.subtract(1, "month").date(cell.day);
              else if (cell.type === "next") cellDate.add(1, "month").date(cell.day);
              else cellDate.date(cell.day);

              const isDisabled = cellDate.isBefore(min, "day");
              const isToday = cellDate.isSame(today, "day");
              const isSelected = selectedMoment && cellDate.isSame(selectedMoment, "day");
              const isOutside = cell.type !== "current";

              return (
                <button
                  key={ci}
                  type="button"
                  className={`rd-cal-cell${isSelected ? " selected" : ""}${isToday ? " today" : ""}${isOutside ? " outside" : ""}${isDisabled ? " disabled" : ""}`}
                  disabled={isDisabled}
                  onClick={() => {
                    if (!isDisabled) onSelect(cellDate.format("YYYY-MM-DD"));
                  }}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export const RecurringDeposits = ({
  recurringDepositAPIData,
  isBankAccountConnected,
  settingUpRecurringDeposit = false,
  onSubmit,
}) => {
  const { register, watch, handleSubmit, setValue } = useFormContext();

  const modalContext = useModalContext();

  const [isSettingUpRecurringDeposit, setIsSettingUpRecurringDeposit] = useState(settingUpRecurringDeposit);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const frequencyProps = register("frequency", {
    required: true,
  });

  const startDate = watch("startDate");
  const frequency = watch("frequency");

  //eslint-disable-next-line
  const recurringDepositFrequencyOptions = useMemo(() => getRecurringDepositFrequencies(startDate), [startDate]);

  const formattedExistingRecurringDeposit = useMemo(() => {
    return {
      active: recurringDepositAPIData?.active ?? false,
      ...(recurringDepositAPIData?.active ?? false
        ? (() => {
            const frequency = getRecurringDepositFrequencies(recurringDepositAPIData?.start_date).find(
              (f) => f.value === recurringDepositAPIData?.frequency_type
            );

            return {
              frequency: `${capitaliseString(frequency.title)} on ${frequency.dueOn}`,
              amount: formatCurrency(recurringDepositAPIData?.amount),
              accountNickName: `${recurringDepositAPIData?.originating_account_name} (...${recurringDepositAPIData?.originating_account_mask})`,
              startDate: formatDate(appendISOTime(recurringDepositAPIData?.start_date), longMonthDateFormat),
            };
          })()
        : {}),
    };
  }, [recurringDepositAPIData]);

  const handleRecurringDepositSetup = () => {
    if (formattedExistingRecurringDeposit?.active) {
      renderConfirmClearRecurringDeposit(modalContext);
    } else if (isBankAccountConnected === false) {
      renderMandateBankAccountAddition(modalContext, {
        title: "Bank Account Required",
        description:
          "To set up a recurring deposit, you must first connect a bank account. Please add your bank account to continue.",
        onSuccess: () => setIsSettingUpRecurringDeposit(true),
      });
    } else {
      setIsSettingUpRecurringDeposit((p) => !p);
    }
  };

  const onRecurringDepositAdditionFormSubmit = () => onSubmit({ startDate, frequency });

  const disableSubmitButton = !(startDate ?? "") || !(frequency ?? "");

  const formattedStartDate = useMemo(() => {
    if (!startDate) return "Select date";
    return moment(startDate).format("ddd, MMM D, YYYY");
  }, [startDate]);

  if (settingUpRecurringDeposit) {
    return (
      <form className="rd-modal-content" onSubmit={handleSubmit(onRecurringDepositAdditionFormSubmit)} autoComplete="off">
        <div className="rd-modal-body">
          <div className="rd-section">
            <span className="rd-section-label">Starts on</span>
            <div className="rd-date-picker-card">
              <button
                type="button"
                className="rd-date-row"
                onClick={() => setCalendarOpen((p) => !p)}
              >
                <div className="rd-date-left">
                  <CalendarIcon />
                  <span className="rd-date-text">{formattedStartDate}</span>
                </div>
                <ChevronRight />
              </button>
              {calendarOpen && (
                <InlineCalendar
                  selectedDate={startDate}
                  minDate={moment().add(1, "days").format("YYYY-MM-DD")}
                  onSelect={(date) => {
                    setValue("startDate", date);
                    setCalendarOpen(false);
                  }}
                />
              )}
            </div>
          </div>

          <div className="rd-section">
            <span className="rd-section-label">Frequency</span>
            <div className="rd-frequency-card">
              {recurringDepositFrequencyOptions.map((opt, idx) => (
                <label
                  key={opt.value}
                  className={`rd-freq-option${frequency === opt.value ? " selected" : ""}${idx < recurringDepositFrequencyOptions.length - 1 ? " has-border" : ""}`}
                >
                  <input
                    type="radio"
                    className="rd-freq-radio-input"
                    id={opt.value}
                    value={opt.value}
                    {...frequencyProps}
                  />
                  <span className={`rd-freq-radio${frequency === opt.value ? " checked" : ""}`} />
                  <div className="rd-freq-info">
                    <span className="rd-freq-title">{opt.title}</span>
                    <span className="rd-freq-desc">{opt.description}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="rd-footer">
            <p className="rd-disclaimer">
              You can cancel or modify your recurring deposit at any time from your account settings.
            </p>
          </div>
        </div>
        <div className="action-container split-equally">
          <PrimaryButton type="submit" disabled={disableSubmitButton}>
            Set
          </PrimaryButton>
        </div>
      </form>
    );
  }

  return (
    <form className="rdp-form" onSubmit={handleSubmit(onRecurringDepositAdditionFormSubmit)} autoComplete="off">
      <div className="rdp-toggle-card">
        <div className="rdp-toggle-info">
          <span className="rdp-toggle-title">Recurring Deposit</span>
          <span className="rdp-toggle-subtitle">Automatically deposit to stake from your bank</span>
        </div>
        <FormSwitch
          checked={formattedExistingRecurringDeposit?.active || isSettingUpRecurringDeposit}
          onClick={handleRecurringDepositSetup}
          ariaLabel="Recurring Deposit"
        />
      </div>

      {!formattedExistingRecurringDeposit?.active && isSettingUpRecurringDeposit && (
        <>
          <div className="rdp-section">
            <span className="rdp-section-label">Starts on</span>
            <div className="rdp-date-card">
              <span className="rdp-date-label">Select date</span>
              <div className="rdp-date-badge">
                <DatePickerField
                  name="startDate"
                  format="ddd, MMM DD, YYYY"
                  defaultValue={moment().add(1, "days")}
                  datePickerProps={{ disablePast: true }}
                  miniField={true}
                  required={true}
                />
              </div>
            </div>
          </div>

          <div className="rdp-section">
            <span className="rdp-section-label">Frequency</span>
            <div className="rdp-freq-card">
              {recurringDepositFrequencyOptions.map((opt, idx) => (
                <React.Fragment key={opt.value}>
                  <label className={`rdp-freq-row${frequency === opt.value ? " selected" : ""}`}>
                    <div className="rdp-freq-row-info">
                      <span className="rdp-freq-row-title">{opt.title}</span>
                      <span className="rdp-freq-row-desc">{opt.description}</span>
                    </div>
                    <input
                      type="radio"
                      className="rdp-freq-radio-input"
                      id={opt.value}
                      value={opt.value}
                      {...frequencyProps}
                    />
                    <span className={`rdp-freq-radio${frequency === opt.value ? " checked" : ""}`} />
                  </label>
                  {idx < recurringDepositFrequencyOptions.length - 1 && <div className="rdp-freq-divider" />}
                </React.Fragment>
              ))}
              <div className="rdp-freq-divider" />
              <p className="rdp-freq-disclaimer">
                Transfers take one business day to process. Transfers set up on a weekend, or holiday, will occur on the next business day.
              </p>
            </div>
          </div>
        </>
      )}

      {formattedExistingRecurringDeposit?.active && (
        <div className="rdp-section">
          <span className="rdp-section-label">Recurring Deposit Summary</span>
          <div className="rdp-summary-card">
            <div className="rdp-summary-item">
              <span className="rdp-summary-label">Transfer Amount</span>
              <span className="rdp-summary-value">{formattedExistingRecurringDeposit?.amount}</span>
            </div>
            <div className="rdp-summary-item">
              <span className="rdp-summary-label">Debit from</span>
              <span className="rdp-summary-value">{formattedExistingRecurringDeposit?.accountNickName}</span>
            </div>
            <div className="rdp-summary-item">
              <span className="rdp-summary-label">Start Date</span>
              <span className="rdp-summary-value">{formattedExistingRecurringDeposit?.startDate}</span>
            </div>
            <div className="rdp-summary-item">
              <span className="rdp-summary-label">Frequency</span>
              <span className="rdp-summary-value">{formattedExistingRecurringDeposit?.frequency}</span>
            </div>
          </div>
        </div>
      )}

      <div className="rdp-spacer" />

      {!formattedExistingRecurringDeposit?.active && isSettingUpRecurringDeposit && (
        <button type="submit" className="rdp-set-btn" disabled={disableSubmitButton}>
          Set
        </button>
      )}
    </form>
  );
};

const RecurringDepositsFormWrapped = withFormProvider(RecurringDeposits, {
  defaultValues: getRecurringDepositDefaultPreferences(),
});
